
import { type FormEvent, useEffect, useRef } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Textarea } from '@ui/components'

import { useChatContext } from '../ChatProvider'

interface ChatInputProps {
	readonly isDisabled: boolean;
	readonly isReasoning?: boolean | undefined;
	readonly isFollowupActive?: boolean | undefined;
	readonly inputValue: string;
	readonly onSubmit: (inputValue: string, isReasoning: boolean) => Promise<void>;
	readonly setChatInputHeight?: (height: number) => void;
	readonly setInputValue: (inputValue: string) => void;
}

export default function ChatInput({
	isDisabled,
	inputValue,
	isReasoning,
	isFollowupActive,
	setInputValue,
	onSubmit,
	setChatInputHeight
}: ChatInputProps) {
	const { type, chatDispatch } = useChatContext()
	const textareaRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (textareaRef.current && setChatInputHeight) {
			setChatInputHeight(textareaRef.current.scrollHeight)
		}
	}, [inputValue])

	async function handleSubmit(e?: FormEvent<HTMLFormElement> | undefined) {
		if (e) {
			e.preventDefault()
		}

		if (isDisabled) {
			return
		}

		chatDispatch({ type: 'setInputValue', value: '' })
		setInputValue('')
		onSubmit(inputValue.trim(), isReasoning ?? false)
	}

	async function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
		if ((event.metaKey || event.altKey || event.ctrlKey) && event.key === 'Enter') {
			setInputValue(inputValue + '\n')
		} else if ((event.shiftKey) && event.key === 'Enter') {
			return
		} else if (event.key === 'Enter') {
			event.preventDefault()
			if (isDisabled) {
				return
			}

			await handleSubmit()
		}
	}

	const submitButtonClasses = [
		'absolute p-1 rounded-md text-gray-500',
		'hover:bg-gray-100 disabled:hover:bg-transparent',
		'right-1 md:right-2 disabled:opacity-40 flex items-center',
		'bottom-[calc(50%-18px)]'
	].join(' ')

	return (
		<div ref={ textareaRef } className="relative px-4 bottom-0 left-0 w-full bg-white">
			<form onSubmit={ handleSubmit }>
				<div className="stretch flex flex-row gap-3 mx-2 md:mx-4">
					<div className="relative flex h-full flex-1 items-stretch md:flex-col ">
						<div className={ type === 'chatbot' ? 'flex flex-col w-full py-2 pl-4 flex-grow md:py-3 md:pl-4 relative border border-black/10 bg-white rounded-md shadow-[0_0_10px_rgba(0,0,0,0.10)]' : 'p-2 min-h-[60px]' }>
							<Textarea
								value={ inputValue }
								style="naked"
								placeholder={ isFollowupActive ? 'Ask a follow-up question' : type === 'chatbot' ? 'Ask me a question' : 'Select a brain or drag & drop a document. Then ask me a question!' }
								customClass={ `${type === 'chatbot' ? 'pr-16' : ''} ` }
								onChange={ value => {
									setInputValue(value)
								} }
								onKeyDown={ handleKeyDown }
							/>
							{ type === 'chatbot' && (
								<button
									disabled={ isDisabled }
									className={ submitButtonClasses }
								>
									<div className={ `h-6 w-6 p-1 flex items-center justify-center ${isDisabled ? 'text-gray-400' : ''}` }>
										<FontAwesomeIcon icon="paper-plane" />
									</div>
								</button>
							) }
						</div>
					</div>
				</div>
			</form>
		</div>
	)
}
